import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, ListChecks, ArrowDownToLine, ArrowUpFromLine, Hash, ChevronRight, History, Gift, ReceiptText, Headphones, ShieldCheck, Pencil, CalendarDays, CheckCircle2, HeartHandshake } from "lucide-react";
import { api } from "@/lib/api";
import { money, dateOnly, initials } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, SandboxNotice } from "@/components/brand";
import TxTable, { TxDetails } from "@/components/TxTable";
import { Skeleton } from "@/components/ui/skeleton";

export const ProfileCard = ({ user }) => (
  <div data-testid="profile-card" className="relative overflow-hidden rounded-2xl border border-cr-line bg-cr-surface p-6 sm:p-8">
    <img src="/brand/ball.jpg" alt="" className="absolute -right-10 top-0 h-full opacity-30 mix-blend-lighten" />
    <div className="relative flex flex-col gap-6 sm:flex-row sm:items-center">
      <div className="relative grid h-28 w-28 shrink-0 place-items-center rounded-full border-2 border-cr-gold font-display text-4xl font-black text-cr-gold">
        {initials(user.full_name)}
        <Link to="/account/profile" aria-label="Edit profile" data-testid="profile-edit-btn" className="absolute bottom-1 right-1 rounded-full bg-cr-elevated p-2"><Pencil className="h-3.5 w-3.5 text-cr-ink" /></Link>
      </div>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-3"><h2 data-testid="profile-name" className="text-2xl font-bold">{user.full_name}</h2><span className="rounded border border-cr-gold/50 px-2 py-0.5 text-xs font-bold text-cr-gold">{user.vip_tier}</span></div>
        <p className="mt-2 flex items-center gap-2 text-cr-ink/85">{user.email}{user.email_verified && <CheckCircle2 className="h-4 w-4 text-cr-success" />}</p>
        {user.phone && <p className="mt-1 text-cr-ink/75">{user.phone}</p>}
        <div className="mt-4 flex flex-wrap gap-6 text-sm">
          <div className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-cr-muted" /><div><p className="text-xs text-cr-muted">Member Since</p><p>{dateOnly(user.created_at)}</p></div></div>
          <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-cr-muted" /><div><p className="text-xs text-cr-muted">Account Status</p><StatusBadge status={user.kyc_status} testid="profile-kyc-status" /></div></div>
        </div>
      </div>
    </div>
  </div>
);

const Stat = ({ icon: Icon, label, value, sub, to, gold, testid }) => (
  <div data-testid={testid} className="card-cr p-5">
    <p className="flex items-center gap-2 text-sm text-cr-ink/85"><Icon className="h-5 w-5 text-cr-gold" />{label}</p>
    <p className={`mt-3 font-display text-2xl font-bold ${gold ? "text-cr-gold" : ""}`}>{value}</p>
    {to ? <Link to={to} className="mt-1 inline-flex items-center gap-1 text-xs text-cr-ink/70 hover:text-cr-gold">{sub}<ChevronRight className="h-3 w-3" /></Link> : <p className="mt-1 text-xs text-cr-muted">{sub}</p>}
  </div>
);

const QUICK = [[ReceiptText, "My Bets", "View your bet history", "/account/bets"], [History, "Transaction History", "View all transactions", "/account/transactions"],
  [Gift, "Bonuses", "View your bonuses", "/account/bonuses"], [HeartHandshake, "Responsible Gaming", "Limits & breaks", "/account/settings"]];

const RightRail = ({ wallet }) => (
  <div className="space-y-6">
    <div data-testid="balance-card" className="card-cr p-6">
      <p className="flex items-center gap-2"><Wallet className="h-5 w-5 text-cr-gold" />Coin Balance</p>
      <p data-testid="overview-balance" className="mt-2 font-display text-3xl font-bold text-cr-gold">{(wallet?.coins || 0).toLocaleString("en-IN")} coins</p>
      <p className="mt-4 text-sm text-cr-ink/80">Worth</p>
      <p data-testid="overview-pending" className="font-display text-lg">{money(wallet?.coins || 0, { whole: true })}</p>
      <div className="mt-5 grid gap-3">
        <Link to="/account/deposit" data-testid="overview-deposit-btn" className="btn-gold py-3">+ Add Coins</Link>
        <Link to="/account/withdraw" data-testid="overview-withdraw-btn" className="btn-outline py-3 text-cr-gold">Withdraw</Link>
      </div>
    </div>
    <div className="card-cr p-5">
      <p className="mb-3 font-display font-semibold">Quick Actions</p>
      {QUICK.map(([I, t, d, to]) => (
        <Link key={t} to={to} data-testid={`quick-${t.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-3 border-b border-cr-line py-3 last:border-0 hover:text-cr-gold">
          <I className="h-5 w-5 text-cr-gold" /><div className="flex-1"><p className="text-sm font-semibold">{t}</p><p className="text-xs text-cr-muted">{d}</p></div><ChevronRight className="h-4 w-4" />
        </Link>
      ))}
    </div>
    <div className="card-cr p-5">
      <p className="font-display font-semibold">Need Help?</p>
      <p className="mt-2 flex items-center gap-2 text-sm text-cr-ink/75"><Headphones className="h-4 w-4" />Open a ticket and track replies.</p>
      <Link to="/account/support" data-testid="overview-contact-support" className="btn-outline mt-4 w-full text-cr-gold">Contact Support</Link>
    </div>
  </div>
);

export default function Overview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [view, setView] = useState(null);
  useEffect(() => { api.get("/me/overview").then((r) => setData(r.data)); }, []);
  return (
    <div data-testid="overview-page" className="grid gap-6 xl:grid-cols-[1fr_320px]">
      <div className="min-w-0 space-y-6">
        <div><h1 className="text-2xl font-bold">Overview</h1><p className="text-sm text-cr-ink/70">Your account at a glance</p></div>
        <SandboxNotice />
        <ProfileCard user={user} />
        {!user.email_verified && <div data-testid="verify-email-banner" className="rounded-lg border border-cr-gold/40 bg-cr-gold/5 p-4 text-sm">Your email is not verified. <Link to="/account/profile?tab=security" className="font-semibold text-cr-gold underline">Verify now</Link></div>}
        <h3 className="text-lg font-semibold">Account Overview</h3>
        {!data ? <Skeleton className="h-28 bg-cr-elevated" /> : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat testid="stat-balance" icon={Wallet} label="Coins" value={(data.wallet.coins || 0).toLocaleString("en-IN")} sub="View Wallet" to="/account/wallet" gold />
            <Stat testid="stat-deposits" icon={ArrowDownToLine} label="Coins Added" value={money(data.total_deposits, { whole: true })} sub={`${data.pending_deposits} pending`} />
            <Stat testid="stat-withdrawals" icon={ArrowUpFromLine} label="Withdrawn" value={money(data.total_withdrawals, { whole: true })} sub="Completed" />
            <Stat testid="stat-tx-count" icon={Hash} label="Transactions" value={data.transaction_count} sub={`${data.total_bets} bets placed`} />
          </div>
        )}
        <div className="flex items-center justify-between"><h3 className="text-lg font-semibold">Recent Activity</h3><Link to="/account/transactions" data-testid="view-all-activity" className="text-sm text-cr-gold">View All Activity →</Link></div>
        {data ? <TxTable items={data.recent_transactions} onView={setView} testid="recent-tx-table" /> : <Skeleton className="h-40 bg-cr-elevated" />}
        <div className="card-cr flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3"><ListChecks className="h-6 w-6 text-cr-gold" /><div><p className="font-semibold">Responsible gaming</p><p className="text-sm text-cr-muted">Set deposit limits, session reminders, cooling-off or self-exclusion.</p></div></div>
          <Link to="/account/settings" data-testid="overview-rg-link" className="btn-outline text-sm">Manage limits</Link>
        </div>
      </div>
      <RightRail wallet={data?.wallet} />
      <TxDetails tx={view} onClose={() => setView(null)} />
    </div>
  );
}
